import { clientV3SDK } from "@istari-frontend/api-client";
import { V3Models } from "@istari/istari-client";
import { queryOptions } from "@tanstack/react-query";

import { DEFAULT_CACHE_TIME } from "~/constants";

import { resourceQueryKeys } from "./resource-query-options";

type ListResourcesByIds = (request: {
  resourceId: string[];
  size: number;
  archiveStatus: V3Models.ArchiveStatus;
}) => Promise<V3Models.CursorPageResourceDto>;

interface ResourceExistenceLoaderOptions {
  flushDelayMs?: number;
  chunkSize?: number;
}

export interface ResourceExistenceLoader {
  load: (resourceId: string) => Promise<V3Models.ResourceDto | null>;
}

const MISS_STALE_TIME = 30 * 1000;

// True when the backend lists the resource; false covers nonexistent and not permitted alike
// (listResources omits both silently). Many chips mount in one commit, so their lookups are
// coalesced into one listResources call instead of a getResource per id.
// A miss is exact for the instant it was taken (the id filter runs a live permission check,
// not the cached allowed-ids list), but the resource can be created or shared afterwards, so
// a miss goes stale sooner than a hit and the next mount re-asks.
export const resourceExistsQueryOptions = (resourceId: string) =>
  queryOptions({
    queryKey: [...resourceQueryKeys.all(), "exists", resourceId],
    queryFn: () => resourceExistenceLoader.load(resourceId),
    enabled: !!resourceId,
    staleTime: (query) => (query.state.data === null ? MISS_STALE_TIME : DEFAULT_CACHE_TIME),
    select: (dto) => dto !== null,
  });

export function createResourceExistenceLoader(
  listResources: ListResourcesByIds,
  { flushDelayMs = 10, chunkSize = 50 }: ResourceExistenceLoaderOptions = {},
): ResourceExistenceLoader {
  let pending = new Map<string, PendingLookup>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    timer = null;
    const batch = pending;
    pending = new Map();
    for (const chunk of chunked([...batch.keys()], chunkSize)) {
      // size must cover the whole chunk: a shorter page would read real ids as missing.
      // The SDK proxy can throw synchronously (e.g. malformed stored auth); routing the call
      // through a promise sends that into the rejection path instead of out of the timer.
      Promise.resolve()
        .then(() => listResources({ resourceId: chunk, size: chunk.length, archiveStatus: V3Models.ArchiveStatus.All }))
        .then((page) => {
          const dtoById = new Map(page.items.map((dto) => [dto.resourceId, dto]));
          chunk.forEach((id) => batch.get(id)?.resolve(dtoById.get(id) ?? null));
        })
        .catch((error: unknown) => chunk.forEach((id) => batch.get(id)?.reject(error)));
    }
  };

  return {
    load: (resourceId) => {
      const existing = pending.get(resourceId);
      if (existing) return existing.promise;
      const lookup = createPendingLookup();
      pending.set(resourceId, lookup);
      timer ??= setTimeout(flush, flushDelayMs);
      return lookup.promise;
    },
  };
}

interface PendingLookup {
  promise: Promise<V3Models.ResourceDto | null>;
  resolve: (dto: V3Models.ResourceDto | null) => void;
  reject: (error: unknown) => void;
}

function createPendingLookup(): PendingLookup {
  let resolve!: PendingLookup["resolve"];
  let reject!: PendingLookup["reject"];
  const promise = new Promise<V3Models.ResourceDto | null>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

function chunked<T>(items: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(items.length / size) }, (_, i) => items.slice(i * size, (i + 1) * size));
}

const resourceExistenceLoader = createResourceExistenceLoader((request) => clientV3SDK.listResources(request));
